import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import ConfirmDialog from "components/ConfirmDialog";
import SuppressNotificationsToggle from "components/SuppressNotificationsToggle";

/**
 * SOUND-618 — one confirmation surface for every application status transition,
 * shared by the registration tab (`ApplicationTab`) and the MID application page
 * (`MidApplication`).
 *
 * Why this exists at all: before it, only transitions with `requiresReason: true`
 * (Reject / Request Info) paused for input. "Approve" fired on a single click —
 * an IRREVERSIBLE transition (`approved` is terminal in both state machines) that
 * also emailed the merchant, with nothing in between. Every transition now
 * confirms, and the suppression opt-out lives in the same place.
 *
 * This component OWNS its ephemeral state (reason, suppressNotifications) and
 * hands the values back via onConfirm. That is deliberate: state initialises when
 * the dialog opens, so the suppression box cannot silently stay ticked from a
 * previous action — the failure mode where a merchant quietly stops being told
 * anything.
 *
 * Copy (`title`, `confirmLabel`) comes from the CALLER rather than being built
 * from a label here: the registration tab's labels are imperative verbs
 * ("Approve", "Reject") while the MID page's are past-tense adjectives
 * ("Approved", "Declined"), so no single template reads correctly for both.
 *
 * @param {string}   targetStatus   status being transitioned to (raw key)
 * @param {string}   title          dialog heading, caller-supplied for grammar
 * @param {string}   confirmLabel   confirm button text
 * @param {string}   statusLabel    human name of the target status, for the body
 * @param {boolean}  requiresReason gate Confirm on a non-empty reason
 * @param {boolean}  terminal       target status has no outbound transitions
 * @param {boolean}  canSuppress    caller is internal (shows the opt-out)
 * @param {Function} onConfirm      ({ reason, suppressNotifications }) => void
 */
export default function ApplicationStatusDialog({
  open,
  targetStatus,
  title,
  confirmLabel = "Confirm",
  statusLabel,
  requiresReason = false,
  terminal = false,
  canSuppress = false,
  submitting = false,
  onConfirm,
  onCancel,
  entityNoun = "application",
}) {
  const [reason, setReason] = useState("");
  const [suppressNotifications, setSuppressNotifications] = useState(false);

  // Reset on every open. Keyed on targetStatus too, so switching straight from
  // one pending action to another cannot carry a suppression choice across.
  useEffect(() => {
    if (open) {
      setReason("");
      setSuppressNotifications(false);
    }
  }, [open, targetStatus]);

  if (!open) return null;

  const blocked = requiresReason && !reason.trim();
  const shownStatus = statusLabel || targetStatus;

  return (
    <ConfirmDialog
      open={open}
      size="md"
      variant={terminal ? "destructive" : "default"}
      title={title}
      description={
        terminal
          ? `This sets the ${entityNoun} to "${shownStatus}" — a final state that cannot be changed back from the portal.`
          : `This sets the ${entityNoun} to "${shownStatus}".`
      }
      confirmLabel={submitting ? "Working..." : confirmLabel}
      confirmDisabled={blocked || submitting}
      onConfirm={() => onConfirm({ reason: reason.trim() || undefined, suppressNotifications })}
      onCancel={onCancel}
    >
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">
          Reason {requiresReason ? "(required)" : "(optional)"}
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={
            requiresReason
              ? "Required — explain the reason for this change"
              : "Optional context for this change"
          }
          rows={3}
          className="input w-full"
          autoFocus
        />
      </div>

      <SuppressNotificationsToggle
        checked={suppressNotifications}
        onChange={setSuppressNotifications}
        disabled={submitting}
        visible={canSuppress}
      />

      {/*
        Spell out the consequence rather than relying on the operator to infer it
        from a ticked box — this is the whole point of the confirmation step.
      */}
      {canSuppress && (
        <p className="text-xs text-muted-foreground">
          {suppressNotifications ? (
            <span className="flex items-start gap-1.5 text-warning">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                The merchant will <strong>not</strong> be notified. The change is still
                recorded in the status history and change log.
              </span>
            </span>
          ) : (
            <>The merchant will be notified by email, Telegram, and partner webhook.</>
          )}
        </p>
      )}
    </ConfirmDialog>
  );
}
